/**
 * ChatWidget — Top-level orchestrator that composes ChatBubble + ChatPanel.
 *
 * Manages the open/closed state of the chat panel and tracks unread messages.
 * Uses the useChat hook for all chat state management.
 * Mounted once in MainLayout so it appears on every authenticated page.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import ChatBubble from './ChatBubble';
import ChatPanel from './ChatPanel';
import useChat from '../../hooks/useChat';
import { usePageContext } from '../../context/PageContext';

/** Welcome message shown on first open with empty conversation. */
const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm the OMS Assistant. I can help you search purchase orders, look up supplier information, and compare pricing. What would you like to know?",
  timestamp: new Date().toISOString()
};

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const { pageContext } = usePageContext();
  const { messages, isLoading, error, sendMessage, resetChat } = useChat(pageContext);

  // Track the previous message count so we know when new assistant
  // messages arrive while the panel is closed.
  const prevCountRef = useRef(messages.length);

  // Detect new assistant messages arriving while the panel is closed
  useEffect(() => {
    if (messages.length > prevCountRef.current) {
      const latest = messages[messages.length - 1];
      // Mark unread only if the panel is closed and the new message is from the assistant
      if (!isOpen && latest?.role === 'assistant') {
        setHasUnread(true);
      }
    }
    prevCountRef.current = messages.length;
  }, [messages, isOpen]);

  /**
   * Toggle the chat panel. Clears unread indicator when opening.
   */
  const handleToggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) setHasUnread(false); // Opening — clear unread
      return !prev;
    });
  }, []);

  /**
   * Close the panel.
   */
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Reset the conversation and inject a fresh welcome message.
   */
  const handleReset = useCallback(async () => {
    await resetChat();
  }, [resetChat]);

  // Build the messages array to display — include welcome message if empty
  const displayMessages = messages.length === 0 ? [WELCOME_MESSAGE] : messages;

  return (
    <>
      {/* Floating action button — always visible */}
      {!isOpen && (
        <ChatBubble onClick={handleToggle} hasUnread={hasUnread} />
      )}

      {/* Chat panel — shown when open */}
      {isOpen && (
        <ChatPanel
          messages={displayMessages}
          isLoading={isLoading}
          onSend={sendMessage}
          onReset={handleReset}
          onClose={handleClose}
        />
      )}
    </>
  );
}
